import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Divider,
  Grid,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Tooltip,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { format } from 'date-fns';
import { formatDate, formatAmount } from '../../../utils/global';
import { AuditLoggers } from '../../../utils/auditLogger';
import { buildEvidenceFromRawHistoryEntries } from '../../../utils/chargebackEvidence';
import {
  buildChargebackBooklet,
  extractSupportingDocumentsFromHistory,
} from '../../../utils/chargebackBooklet';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonatePlainIconButtonSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getTenantCompanyName = () =>
  localStorage.getItem('tenant_name')?.trim() || 'Company';

const getLetterDateDisplay = () => format(new Date(), 'MM-dd-yyyy');

const getLetterReference = (caseData, chargebackId) =>
  String(caseData?.CASE_NO ?? caseData?.CASE_NUMBER ?? chargebackId ?? '—');

const getLetterSubject = (caseData) => {
  const caseNo = caseData?.CASE_NO != null ? String(caseData.CASE_NO) : '';
  const proc = caseData?.PROCESSOR != null ? String(caseData.PROCESSOR) : '';
  const parts = ['Chargeback dispute filing'];
  if (proc) parts.push(proc);
  if (caseNo) parts.push(`Case ${caseNo}`);
  return parts.join(' — ') || 'Chargeback dispute filing';
};

/** Wraps body HTML in company letterhead: Company name, Heading, Date, Reference, Subject, then body (no "Body" label in output). */
const buildFormalLetterHtml = ({
  companyName,
  heading,
  letterDate,
  reference,
  subject,
  bodyInnerHtml,
}) => {
  const cn = escapeHtml(companyName || getTenantCompanyName());
  const h = escapeHtml(heading || 'Chargeback Dispute Filing Letter');
  const d = escapeHtml(letterDate || getLetterDateDisplay());
  const r = escapeHtml(reference || '—');
  const s = escapeHtml(subject || '');
  return `<div class="dispute-letter-formal">
<p style="margin:0 0 12px;font-size:1.1rem;font-weight:700;text-align:right;">${cn}</p>
<h2 style="margin:8px 0 20px;text-align:center;font-size:1.35rem;font-weight:700;border-bottom:1px solid #e0e0e0;padding-bottom:12px;">${h}</h2>
<p style="margin:6px 0;"><strong>Date:</strong> ${d}</p>
<p style="margin:6px 0;"><strong>Reference:</strong> ${r}</p>
<p style="margin:6px 0;"><strong>Subject:</strong> ${s}</p>
<div class="dispute-letter-body" style="margin-top:20px;">${bodyInnerHtml}</div>
</div>`;
};

const getDefaultBodyInnerHtml = () =>
  `<p><strong>To Whom It May Concern,</strong></p>
<p>This letter serves as formal notification of our dispute regarding the chargeback for the above-referenced transaction.</p>
<p>We believe this chargeback is invalid for the following reasons:</p>
<ul>
<li>The transaction was properly authorized and completed.</li>
<li>The customer received the goods/services as described.</li>
<li>All terms of service were clearly communicated and agreed upon.</li>
</ul>
<p>We have attached all relevant documentation to support our position, including:</p>
<ul>
<li>Proof of delivery/service</li>
<li>Transaction authorization records</li>
<li>Customer communication history</li>
<li>Any additional supporting documents</li>
</ul>
<p>We respectfully request that this chargeback be reversed and the funds be returned to our account. Please let us know if you require any additional information to process this dispute.</p>
<p>Sincerely,<br><strong>Your Name</strong><br>Dispute Resolution Department</p>`;

const getDefaultLetterContent = (caseData, chargebackId) =>
  buildFormalLetterHtml({
    companyName: getTenantCompanyName(),
    heading: 'Chargeback Dispute Filing Letter',
    letterDate: getLetterDateDisplay(),
    reference: getLetterReference(caseData, chargebackId),
    subject: getLetterSubject(caseData),
    bodyInnerHtml: getDefaultBodyInnerHtml(),
  });

// ReactQuill toolbar configuration
const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link'],
    [{ 'align': [] }],
    ['clean']
  ],
};

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'link', 'align'
];

const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatAiDraftToHtml = (draftText = '') => {
  const cleanText = String(draftText || '').trim();
  if (!cleanText) return '';

  const sentences = cleanText
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+(?=[A-Z])/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

  if (sentences.length === 0) return '';

  const paragraphs = [];
  for (let i = 0; i < sentences.length; i += 2) {
    paragraphs.push(`${sentences[i]}${sentences[i + 1] ? ` ${sentences[i + 1]}` : ''}`);
  }

  return paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join('');
};

const parseMoney = (v) => {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** Initial attempt + 2 retries; helps when draft-letter Lambda is cold-starting. */
const DRAFT_LETTER_MAX_ATTEMPTS = 3;
const DRAFT_LETTER_RETRY_DELAY_MS = 5000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableDraftError = (error) => {
  const status = error?.status;
  if (status == null) return true; // network / aborted / unexpected
  return status === 500 || status === 502 || status === 503 || status === 504;
};

/** Match CaseDetails / API: STATUS values like PENDING for CHARGEBACK_HISTORY. */
const normalizeHistoryStatusForDb = (status) => {
  if (status == null || status === '') return 'PENDING';
  return String(status).trim().toUpperCase().replace(/\s+/g, '_');
};

export default function DisputeLetter() {
  const { canEdit, canExport } = useFunctionPermissions(FC.MERCHANT_DATA_HUB);
  const location = useLocation();
  const navigate = useNavigate();
  const caseData = location.state?.caseData;
  const initialSelectedProperty = location.state?.selectedProperty || null;
  
  // FIXED: Get chargebackId with proper fallback to caseData.ID
  const chargebackId = location.state?.chargebackId || caseData?.ID || caseData?.CASE_NO;
  
  // Storage key for saving edited letter
  const storageKey = `dispute_letter_${chargebackId}`;
  
  // Letter HTML is empty until user opens the editor or generates AI (no sample letter on load)
  const [letterContent, setLetterContent] = useState('');
  const [showLetterSection, setShowLetterSection] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [draftRetryAttempt, setDraftRetryAttempt] = useState(0);
  const [isDraftReady, setIsDraftReady] = useState(true);
  const [latestAiLetterContent, setLatestAiLetterContent] = useState('');
  const [savingToHistory, setSavingToHistory] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState('');
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(initialSelectedProperty);
  const [propertyLogoDataUrl, setPropertyLogoDataUrl] = useState('');
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [submittingChase, setSubmittingChase] = useState(false);
  const [bookletProgress, setBookletProgress] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [draftError, setDraftError] = useState('');

  const processorUpper = String(caseData?.PROCESSOR || '').trim().toUpperCase();
  const isChaseProcessor =
    processorUpper === 'CHASE' ||
    processorUpper === 'CHASE_NAP' ||
    processorUpper.startsWith('CHASE');
  const letterSavedInHistory = historyRows.some(
    (row) => String(row?.ACTION || row?.action || '').toUpperCase() === 'DISPUTE_LETTER_SAVED'
  );
  const convertedFiles = historyRows
    .filter((row) => String(row?.ACTION || row?.action || '').toUpperCase() === 'CONVERSION_FINISHED')
    .map((row) => {
      const key = String(row?.FILE_LOCATION || row?.file_location || '').trim();
      const name = String(row?.FILE_NAME || row?.file_name || '').trim();
      if (!key) return null;
      return {
        converted_ref: key.startsWith('s3://') ? key : key,
        file_key: key,
        file_name: name || key.split('/').pop(),
      };
    })
    .filter(Boolean);
  const hasEvidenceUploads = historyRows.some((row) => {
    const action = String(row?.ACTION || row?.action || '').toUpperCase();
    const loc = String(row?.FILE_LOCATION || row?.file_location || '').trim();
    return action === 'DOCUMENT_UPLOADED' && !!loc;
  });
  // Tommy: Submit must work with letter only (no evidence). If evidence was
  // uploaded, require conversion first.
  const evidenceReadyForSubmit = !hasEvidenceUploads || convertedFiles.length > 0;
  const alreadyQueuedOrSubmitted = historyRows.some((row) => {
    const action = String(row?.ACTION || row?.action || '').toUpperCase();
    return action === 'DISPUTE_SUBMITTED' || action === 'CHASE_SUBMIT_QUEUED';
  });
  const canSubmitChase =
    isChaseProcessor &&
    canEdit &&
    letterSavedInHistory &&
    evidenceReadyForSubmit &&
    !alreadyQueuedOrSubmitted &&
    !submittingChase;

  const submitTooltip = !canEdit
    ? 'You do not have permission to submit'
    : !isChaseProcessor
      ? 'Submit is only for Chase cases (not Elavon or Amex)'
      : !letterSavedInHistory
        ? 'Save the dispute letter before Submit'
        : hasEvidenceUploads && convertedFiles.length === 0
          ? 'Convert supporting documents on Case Details first, then Submit'
          : alreadyQueuedOrSubmitted
            ? 'Already submitted — one Chase case can be queued only once'
            : submittingChase
              ? 'Queuing for Chase daily submit…'
              : hasEvidenceUploads
                ? 'Queue this Chase case for the daily submit (nothing is sent yet)'
                : 'Queue this Chase case (letter only — no evidence uploaded)';

  const showToast = (message, severity = 'success', duration = 6000) => {
    setToast({ open: true, message, severity, duration });
  };

  const handleCloseToast = (_event, reason) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  };
  
  // Load saved letter for this chargeback only; otherwise no letter block until Edit / AI
  useEffect(() => {
    if (!chargebackId) return;
    const savedContent = localStorage.getItem(storageKey);
    if (savedContent) {
      setLetterContent(savedContent);
      setHasChanges(true);
      setShowLetterSection(true);
    } else {
      setLetterContent('');
      setHasChanges(false);
      setLatestAiLetterContent('');
      setShowLetterSection(false);
    }
    setIsDraftReady(true);
  }, [chargebackId, storageKey]);

  useEffect(() => {
    const fetchSelectedProperty = async () => {
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');
        const selectedPropertyId =
          initialSelectedProperty?.propertyId ||
          initialSelectedProperty?.PROPERTY_ID ||
          localStorage.getItem('normalized_chargebacks_selected_property_id');

        if (!token || !tenantId || !selectedPropertyId) return;

        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/by-tenant?tenant_id=${encodeURIComponent(tenantId)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!resp.ok) return;

        const list = await resp.json();
        if (!Array.isArray(list)) return;
        const match = list.find(
          (p) => String(p?.propertyId || '') === String(selectedPropertyId)
        );
        if (match) {
          setSelectedProperty({
            propertyId: match.propertyId,
            propertyName: match.propertyName || '',
            propertyCode: match.propertyCode || '',
            logoUrl: match.logoUrl || '',
            logoS3Key: match.logoS3Key || '',
          });
        }
      } catch (e) {
        console.warn('Could not resolve selected property for branding:', e);
      }
    };

    fetchSelectedProperty();
  }, [initialSelectedProperty]);

  useEffect(() => {
    const fetchSupportingDocuments = async () => {
      try {
        if (!chargebackId) return;
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const tenantIntegration = localStorage.getItem('tenant_integration');
        if (!token || !tenantIntegration) return;

        const resp = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/${encodeURIComponent(chargebackId)}/history/`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'X-Tenant-ID': tenantIntegration,
            },
          }
        );
        if (!resp.ok) return;
        const payload = await resp.json().catch(() => ({}));
        const rows = Array.isArray(payload?.data) ? payload.data : [];
        setHistoryRows(rows);
        setSupportingDocuments(extractSupportingDocumentsFromHistory(rows));
      } catch (e) {
        console.warn('Failed to load supporting documents for booklet:', e);
      }
    };
    fetchSupportingDocuments();
  }, [chargebackId]);

  const handleChaseSubmit = async () => {
    if (!canSubmitChase) return;
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      const userEmail = localStorage.getItem('username') || 'system';
      if (!token || !tenantIntegration) {
        throw new Error('Authentication or tenant information is missing.');
      }

      const card = String(caseData?.CARDHOLDER_NO || caseData?.CARD_NUMBER || '');
      const last4 = card.replace(/\D/g, '').slice(-4);
      if (last4.length !== 4) {
        showToast('Card last 4 digits are required before Chase submit.', 'warning');
        return;
      }

      let pde = '';
      const attrs = caseData?.PROCESSOR_ATTRIBUTES;
      if (attrs && typeof attrs === 'object') {
        pde = String(attrs.sequence_number || attrs.SEQUENCE_NUMBER || '');
      } else if (typeof attrs === 'string' && attrs.trim()) {
        try {
          const parsed = JSON.parse(attrs);
          pde = String(parsed?.sequence_number || parsed?.SEQUENCE_NUMBER || '');
        } catch (_e) {
          pde = '';
        }
      }

      setSubmittingChase(true);
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/${encodeURIComponent(chargebackId)}/chase-submit/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantIntegration,
          },
          body: JSON.stringify({
            processor: caseData?.PROCESSOR || 'CHASE',
            converted_files: convertedFiles,
            last4,
            pde_sequence_number: pde || undefined,
            division_number: caseData?.DIVISION_NUMBER || caseData?.DIVISION || undefined,
            case_ref: String(caseData?.CASE_NO || chargebackId),
            created_by: userEmail,
          }),
        }
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(body?.detail || `Chase submit failed (${resp.status})`);
      }

      const statusSource =
        caseData?.CHARGEBACK_WIN_LOSS ||
        caseData?.CHARGEBACK_STATUS ||
        caseData?.status ||
        'PENDING';
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantIntegration,
          },
          body: JSON.stringify({
            chargeback_id_fk: String(chargebackId),
            action: 'CHASE_SUBMIT_QUEUED',
            status: normalizeHistoryStatusForDb(statusSource),
            message: `Queued for Chase daily submit (${body.queued_count || convertedFiles.length} file(s))`,
            created_by: userEmail,
            file_location: '',
            file_name: '',
          }),
        }
      );

      setHistoryRows((prev) => [
        {
          ACTION: 'CHASE_SUBMIT_QUEUED',
          action: 'CHASE_SUBMIT_QUEUED',
          MESSAGE: `Queued for Chase daily submit (${body.queued_count || convertedFiles.length} file(s))`,
        },
        ...prev,
      ]);
      showToast('Case queued for Chase daily submit. Nothing was sent yet.', 'success');
    } catch (e) {
      showToast(e?.message || 'Failed to queue Chase submit', 'error');
    } finally {
      setSubmittingChase(false);
    }
  };

  const fetchAttachmentBlob = async (att) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/files/download?fileKey=${encodeURIComponent(att.key)}${att.name ? `&fileName=${encodeURIComponent(att.name)}` : ''}`;
    const resp = await fetch(downloadUrl, {
      method: 'GET',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!resp.ok) {
      throw new Error(`Failed to download attachment "${att.name}" (${resp.status})`);
    }
    const blob = await resp.blob();
    return {
      blob,
      contentType: resp.headers.get('content-type') || '',
      fileName: att.name,
    };
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const resolvePropertyLogoDataUrl = async () => {
    if (propertyLogoDataUrl) return propertyLogoDataUrl;
    const logoUrl = selectedProperty?.logoUrl;
    const logoS3Key = selectedProperty?.logoS3Key;
    if (!logoUrl && !logoS3Key) return '';

    try {
      // Prefer backend proxy download to avoid browser-side S3 CORS restrictions.
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const downloadUrl = logoS3Key
        ? `${process.env.REACT_APP_BACKEND_URL}conductor/files/download?fileKey=${encodeURIComponent(logoS3Key)}`
        : logoUrl;
      const resp = await fetch(downloadUrl, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!resp.ok) return '';
      const blob = await resp.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      setPropertyLogoDataUrl(dataUrl);
      return dataUrl;
    } catch (e) {
      console.warn('Property logo fetch failed for PDF branding:', e);
      return '';
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleGenerateAiLetter = async () => {
    if (!canEdit) return;
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      if (!token || !tenantIntegration) {
        throw new Error('Authentication or tenant information is missing.');
      }

      setIsDraftLoading(true);
      setDraftRetryAttempt(0);
      setDraftError('');
      const generatedBodyHtml = await fetchDraftLetterFromLambda(token, tenantIntegration, {
        onRetry: (attempt) => setDraftRetryAttempt(attempt),
      });
      const wrappedLetter = buildFormalLetterHtml({
        companyName: getTenantCompanyName(),
        heading: 'Chargeback Dispute Filing Letter',
        letterDate: getLetterDateDisplay(),
        reference: getLetterReference(caseData, chargebackId),
        subject: getLetterSubject(caseData),
        bodyInnerHtml: generatedBodyHtml,
      });
      setLetterContent(wrappedLetter);
      setLatestAiLetterContent(wrappedLetter);
      setShowLetterSection(true);
      setIsDraftReady(true);
      setHasChanges(false);
      setIsEditing(false);

      setDraftError('');
      showToast('AI letter generated successfully!', 'success');
    } catch (error) {
      console.error('Failed to generate AI draft:', error);
      const message = error.message || 'Failed to generate AI letter';
      if (error.unmaskedPan) {
        setDraftError(message);
        if (!latestAiLetterContent && !hasChanges) {
          setIsEditing(false);
          setShowLetterSection(false);
          setLetterContent('');
        }
      } else {
        setDraftError('');
        showToast(message, 'error');
      }
    } finally {
      setIsDraftLoading(false);
      setDraftRetryAttempt(0);
    }
  };

  const handleContentChange = (content) => {
    setLetterContent(content);
  };

  const handleSaveLetterToHistory = async () => {
    if (!canEdit) return;
    const html = String(letterContent || '').trim();
    if (!html) {
      showToast('Nothing to save. Add letter content or generate an AI letter first.', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      const userEmail = localStorage.getItem('username') || 'system';

      if (!token || !tenantIntegration) {
        throw new Error('Authentication or tenant information is missing.');
      }
      if (!chargebackId) {
        throw new Error('No chargeback ID found. Cannot save to history.');
      }

      setSavingToHistory(true);

      const statusSource =
        caseData?.CHARGEBACK_WIN_LOSS ||
        caseData?.CHARGEBACK_STATUS ||
        caseData?.status ||
        'PENDING';

      const payload = {
        chargeback_id_fk: String(chargebackId),
        action: 'DISPUTE_LETTER_SAVED',
        status: normalizeHistoryStatusForDb(statusSource),
        message: caseData?.CASE_NO
          ? `Dispute letter saved for case ${caseData.CASE_NO}`
          : 'Dispute letter saved',
        letter_html: html,
        created_by: userEmail,
        file_location: '',
        file_name: `dispute-letter-${caseData?.CASE_NO || chargebackId}.html`,
      };

      const historyResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantIntegration,
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await historyResponse.json().catch(() => ({}));
      if (!historyResponse.ok) {
        const detailMsg = Array.isArray(responseData?.detail)
          ? responseData.detail.map((d) => d.msg || d).join('; ')
          : responseData?.detail;
        throw new Error(detailMsg || responseData?.message || historyResponse.statusText || 'Failed to save to history');
      }

      if (chargebackId) {
        localStorage.setItem(storageKey, letterContent);
        setHasChanges(true);
      }
      setIsEditing(false);
      setShowLetterSection(true);

      // Refresh local history so Submit can enable without a full page reload.
      setHistoryRows((prev) => [
        {
          ACTION: 'DISPUTE_LETTER_SAVED',
          action: 'DISPUTE_LETTER_SAVED',
          MESSAGE: payload.message,
          message: payload.message,
        },
        ...prev,
      ]);

      showToast('Letter saved to case history.', 'success');
      if (!isChaseProcessor) {
        showToast('Submit is only for Chase cases.', 'info', 4000);
      } else if (hasEvidenceUploads && convertedFiles.length === 0) {
        showToast(
          'Letter saved. Convert supporting documents on Case Details before Submit enables.',
          'info',
          7000
        );
      }
    } catch (error) {
      console.error('Save letter to history failed:', error);
      showToast(error.message || 'Failed to save letter to history', 'error');
    } finally {
      setSavingToHistory(false);
    }
  };

  const fetchDraftLetterFromLambda = async (token, tenantIntegration, { onRetry } = {}) => {
    const historyResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/${encodeURIComponent(chargebackId)}/history/`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantIntegration
        }
      }
    );

    const historyPayload = await historyResponse.json().catch(() => ({}));
    const historyEntries = Array.isArray(historyPayload?.data) ? historyPayload.data : [];

    if (!historyResponse.ok) {
      throw new Error(historyPayload.detail || historyResponse.statusText || 'Failed to fetch uploaded evidence');
    }

    const evidenceRaw = buildEvidenceFromRawHistoryEntries(historyEntries);
    const evidence = evidenceRaw.map(({ s3_key, label, note }) => {
      const row = { s3_key };
      if (label) row.label = label;
      if (note) row.note = note;
      return row;
    });

    const draftRequestPayload = {
      action: 'draft',
      chargeback_id: String(chargebackId),
      payload: {
        tenant_id: String(tenantIntegration),
        chargeback_context: {
          case_no: String(caseData?.CASE_NO ?? String(chargebackId)),
          processor: caseData?.PROCESSOR != null ? String(caseData.PROCESSOR) : null,
          reason_code: caseData?.REASON_CODE != null ? String(caseData.REASON_CODE) : null,
          reason: caseData?.REASON != null ? String(caseData.REASON) : null,
          transaction_amount: parseMoney(caseData?.TRANSACTION_AMOUNT),
          chargeback_amount: parseMoney(
            caseData?.CHARGEBACK_AMOUNT ?? caseData?.TRANSACTION_AMOUNT
          ),
        },
        evidence,
      },
    };

    let lastError;
    for (let attempt = 1; attempt <= DRAFT_LETTER_MAX_ATTEMPTS; attempt += 1) {
      try {
        const draftResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/draft-letter/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'X-Tenant-ID': tenantIntegration
            },
            body: JSON.stringify(draftRequestPayload)
          }
        );

        const draftPayload = await draftResponse.json().catch(() => ({}));
        const detailMsg = Array.isArray(draftPayload?.detail)
          ? draftPayload.detail.map((d) => d.msg || d).join('; ')
          : draftPayload?.detail;
        if (!draftResponse.ok || !draftPayload?.draft_paragraph) {
          const baseMessage =
            detailMsg || draftPayload?.message || draftResponse.statusText || 'Failed to generate AI draft letter';
          const unmaskedPan = /unmasked card number/i.test(String(baseMessage));
          const err = new Error(
            unmaskedPan
              ? `${baseMessage} Remove that file from the case history, then generate the letter again.`
              : baseMessage
          );
          err.status = draftResponse.status;
          err.unmaskedPan = unmaskedPan;
          throw err;
        }

        return formatAiDraftToHtml(draftPayload.draft_paragraph);
      } catch (error) {
        lastError = error;
        const canRetry =
          attempt < DRAFT_LETTER_MAX_ATTEMPTS && isRetryableDraftError(error);
        if (!canRetry) throw error;

        console.warn(
          `Draft letter attempt ${attempt}/${DRAFT_LETTER_MAX_ATTEMPTS} failed; retrying in ${DRAFT_LETTER_RETRY_DELAY_MS / 1000}s…`,
          error
        );
        if (typeof onRetry === 'function') onRetry(attempt);
        await sleep(DRAFT_LETTER_RETRY_DELAY_MS);
      }
    }

    throw lastError || new Error('Failed to generate AI draft letter');
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPdfPreviewOpen(false);
    setPdfPreviewFilename('');
  };

  const handlePreviewPdf = async () => {
    const contentForPdf = String(letterContent || '').trim();
    if (!contentForPdf) {
      showToast('Letter content is empty. Add text or generate an AI letter before preview.', 'warning');
      return;
    }
    if (!isDraftReady || isDraftLoading || savingToHistory) return;

    try {
      setPdfPreviewLoading(true);
      const { blob, filename } = await buildBookletOrLetterBlob(contentForPdf);
      const url = URL.createObjectURL(blob);
      setPdfPreviewFilename(filename || 'preview.pdf');
      setPdfPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setPdfPreviewOpen(true);
    } catch (error) {
      console.error('PDF preview failed:', error);
      showToast(error.message || 'Failed to generate PDF preview', 'error');
    } finally {
      setPdfPreviewLoading(false);
      setBookletProgress(null);
    }
  };

  /**
   * Build a single PDF Blob containing the dispute letter and (when present)
   * every supporting document uploaded for the case, in booklet form.
   * Falls back to the letter-only PDF when there are no attachments.
   */
  const buildBookletOrLetterBlob = async (letterHtml) => {
    const pdfModule = await import('../../../utils/generatePdf');
    const generatePdf = pdfModule.default;
    const logoDataUrl = await resolvePropertyLogoDataUrl();
    const letterResult = await generatePdf(caseData, letterHtml, {
      disposition: 'blob',
      propertyLogoDataUrl: logoDataUrl,
      selectedPropertyName:
        selectedProperty?.propertyName || selectedProperty?.propertyCode || '',
    });
    if (!letterResult?.blob) {
      throw new Error('Could not build dispute letter PDF.');
    }

    if (!supportingDocuments || supportingDocuments.length === 0) {
      return { blob: letterResult.blob, filename: letterResult.filename };
    }

    setBookletProgress({ index: 0, total: supportingDocuments.length, status: 'starting' });
    const merged = await buildChargebackBooklet({
      letterBlob: letterResult.blob,
      attachments: supportingDocuments,
      fetchAttachment: fetchAttachmentBlob,
      onProgress: setBookletProgress,
      baseFilename: `Chargeback_${caseData?.CASE_NO || 'case'}_Booklet`,
    });
    return { blob: merged.blob, filename: merged.filename };
  };

  const handleGeneratePdf = async () => {
    if (!canExport) return;
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const tenantIntegration = localStorage.getItem('tenant_integration');
      const userEmail = localStorage.getItem('username') || 'demo@demo.com';
      
      // Log the PDF download action
      try {
        await AuditLoggers.chargebackDownloadPdf(
          caseData?.CASE_NO || 'N/A',
          chargebackId,
          {
            userId: userEmail,
            tenantIntegration,
            caseStatus: caseData?.status || 'UNKNOWN',
            amount: caseData?.amount,
            currency: caseData?.currency,
            documentType: 'dispute_letter'
          }
        );
      } catch (logError) {
        console.error('Failed to log PDF download:', logError);
        // Don't fail the operation if logging fails
      }

      // FIXED: Better logging to debug the issue
      console.log('=== PDF Generation Debug ===');
      console.log('Chargeback ID from state:', location.state?.chargebackId);
      console.log('Chargeback ID from caseData.ID:', caseData?.ID);
      console.log('Final Chargeback ID being used:', chargebackId);
      console.log('Full Case Data:', caseData);
      console.log('===========================');

      if (!chargebackId) {
        throw new Error('No chargeback ID found. Cannot generate PDF.');
      }

      const contentForPdf = String(letterContent || '').trim();
      if (!contentForPdf) {
        throw new Error('Letter content is empty. Add text or click Generate AI Letter before downloading.');
      }

      // FIXED: Ensure we're using the correct chargeback ID (not row ID)
      // The chargeback ID should be in format like "ELAVON|70645", not a numeric row ID
      const payload = {
        chargeback_id_fk: String(chargebackId),  // This should be "ELAVON|70645", not "row-123"
        action: 'GENERATE_FILING_PDF',
        status: 'PENDING',
        message: 'Download PDF',
        notes: 'Dispute letter PDF downloaded',
        created_by: userEmail,
        file_location: '',
        file_name: ''
      };

      console.log('Sending payload:', JSON.stringify(payload, null, 2));

      // First, add history entry for PDF download
      const historyResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/chargebacks/${tenantIntegration}/history/`, 
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantIntegration
          },
          body: JSON.stringify(payload)
        }
      );

      const responseData = await historyResponse.json().catch(() => ({}));
      
      if (!historyResponse.ok) {
        console.error('History API Error:', responseData);
        throw new Error(`Failed to update history: ${responseData.detail?.[0]?.msg || historyResponse.statusText}`);
      }

      console.log('History updated successfully:', responseData);

      if (!isDraftReady) {
        throw new Error('Letter is not ready. Please wait a moment and try again.');
      }

      const { blob, filename } = await buildBookletOrLetterBlob(contentForPdf);
      triggerBlobDownload(blob, filename);

      const attachmentCount = supportingDocuments?.length || 0;
      const msg = attachmentCount > 0
        ? `Booklet downloaded (letter + ${attachmentCount} supporting document${attachmentCount === 1 ? '' : 's'}).`
        : 'PDF generated successfully!';
      showToast(msg, 'success');

    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast(error.message || 'Failed to generate PDF', 'error');
    } finally {
      setBookletProgress(null);
    }
  };

  if (!caseData) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3 }}>
        <Alert severity="error">No case data available</Alert>
        <Button onClick={handleBack} sx={{ ...harmonateUploadOutlinedSx, mt: 2 }}>Go Back</Button>
      </Box>
    );
  }

  if (!chargebackId) {
    return (
      <Box sx={{ ...harmonatePageSx, p: 3 }}>
        <Alert severity="error">
          No chargeback ID found. Cannot generate dispute letter.
          <br />
          <Typography variant="caption">
            Debug info: caseData.ID = {caseData?.ID || 'undefined'}, 
            location.state.chargebackId = {location.state?.chargebackId || 'undefined'}
          </Typography>
        </Alert>
        <Button onClick={handleBack} sx={{ ...harmonateUploadOutlinedSx, mt: 2 }}>Go Back</Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...harmonatePageSx,
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        display: 'block',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ ...harmonatePlainIconButtonSx, mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h1" sx={harmonatePageTitleSx}>
            Dispute Letter - {caseData.CASE_NO}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                onClick={handleGenerateAiLetter}
                disabled={isDraftLoading || !canEdit}
                sx={harmonateUploadOutlinedSx}
              >
                Generate AI Letter
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveLetterToHistory}
                disabled={isDraftLoading || savingToHistory || !String(letterContent || '').trim() || !canEdit}
                sx={harmonateUploadOutlinedSx}
              >
                {savingToHistory ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  setShowLetterSection(true);
                  setIsEditing(true);
                  if (!String(letterContent || '').trim()) {
                    setLetterContent(getDefaultLetterContent(caseData, chargebackId));
                    setHasChanges(false);
                    setLatestAiLetterContent('');
                  }
                }}
                disabled={isDraftLoading || !isDraftReady || !canEdit}
                sx={harmonateUploadOutlinedSx}
              >
                Edit Letter
              </Button>
              <Button
                variant="outlined"
                onClick={handleGenerateAiLetter}
                disabled={isDraftLoading || !isDraftReady || !canEdit}
                sx={harmonateUploadOutlinedSx}
              >
                Generate AI Letter
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveLetterToHistory}
                disabled={isDraftLoading || savingToHistory || !String(letterContent || '').trim() || !canEdit}
                sx={harmonateUploadOutlinedSx}
              >
                {savingToHistory ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={handlePreviewPdf}
            disabled={
              isDraftLoading ||
              !isDraftReady ||
              savingToHistory ||
              pdfPreviewLoading ||
              !String(letterContent || '').trim()
            }
            sx={harmonateUploadOutlinedSx}
          >
            {pdfPreviewLoading ? 'Preparing preview…' : 'Preview PDF'}
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleGeneratePdf}
            disabled={
              isDraftLoading ||
              !isDraftReady ||
              savingToHistory ||
              pdfPreviewLoading ||
              !String(letterContent || '').trim() ||
              !canExport
            }
            sx={harmonateProceedButtonSx}
          >
            {supportingDocuments?.length > 0
              ? `Download PDF (${supportingDocuments.length} doc${supportingDocuments.length === 1 ? '' : 's'} bundled)`
              : 'Download PDF'}
          </Button>
          <Tooltip title={submitTooltip} arrow>
            <span>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleChaseSubmit}
                disabled={!canSubmitChase}
                sx={harmonateProceedButtonSx}
              >
                {submittingChase ? 'Submitting…' : 'Submit'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {supportingDocuments?.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The downloaded PDF will be a booklet: dispute letter followed by{' '}
          {supportingDocuments.length} supporting document
          {supportingDocuments.length === 1 ? '' : 's'} uploaded for this case.
        </Alert>
      )}

      <Dialog
        open={pdfPreviewOpen}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        aria-labelledby="pdf-preview-dialog-title"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle id="pdf-preview-dialog-title" sx={harmonateDialogTitleSx}>
          PDF preview{pdfPreviewFilename ? ` — ${pdfPreviewFilename}` : ''}
        </DialogTitle>
        <DialogContent
          sx={{
            ...harmonateDialogContentSx,
            height: { xs: '65vh', sm: '72vh' },
            p: 0,
            bgcolor: '#525659',
          }}
        >
          {pdfPreviewUrl ? (
            <iframe
              title="Dispute letter PDF preview"
              src={`${pdfPreviewUrl}#toolbar=1`}
              width="100%"
              height="100%"
              style={{ border: 'none', minHeight: '100%', display: 'block' }}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
              <CircularProgress sx={{ color: 'common.white' }} />
              {bookletProgress && bookletProgress.total > 0 && (
                <Typography variant="body2" sx={{ color: 'common.white' }}>
                  Building booklet: attachment {Math.min(bookletProgress.index + 1, bookletProgress.total)} of {bookletProgress.total}
                  {bookletProgress.name ? ` — ${bookletProgress.name}` : ''}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${ht.line}` }}>
          <Button onClick={handleClosePdfPreview} variant="outlined" sx={harmonateCancelButtonSx}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={async () => {
              handleClosePdfPreview();
              await handleGeneratePdf();
            }}
            sx={harmonateProceedButtonSx}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debug info - Remove in production */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="caption">
          <strong>Debug Info:</strong> Chargeback ID = {chargebackId}
        </Typography>
      </Alert>

      <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 4, mb: 4, overflow: 'visible' }}>
        {draftError && !isDraftLoading && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDraftError('')}>
            {draftError}
          </Alert>
        )}
        {isDraftLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 480,
              gap: 2,
            }}
            aria-busy={isDraftLoading}
            aria-live="polite"
          >
            <CircularProgress size={48} />
            <Typography variant="h6" color="text.secondary" align="center">
              {draftRetryAttempt > 0
                ? `Retrying AI letter generation (${draftRetryAttempt}/${DRAFT_LETTER_MAX_ATTEMPTS - 1})…`
                : 'Generating AI dispute letter…'}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 440 }}>
              {draftRetryAttempt > 0
                ? 'The draft service did not respond in time (often a cold start). Waiting briefly and trying again.'
                : 'Pulling uploaded supporting documents from case history and calling the draft-letter service. This may take a few moments.'}
            </Typography>
          </Box>
        ) : (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>Chargeback Dispute Filing Letter</Typography>
          
          <Grid container spacing={2} sx={{ mt: 2, mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Case Number</Typography>
              <Typography>{caseData.CASE_NO || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Status</Typography>
              <Typography>{caseData.CHARGEBACK_WIN_LOSS || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Processed Date</Typography>
              <Typography>{formatDate(caseData.TRANSACTION_DATE)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Merchant ID</Typography>
              <Typography>{caseData.MERCHANT_ID || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Merchant Name</Typography>
              <Typography>{caseData.MERCHANT_NAME || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2">Transaction Amount</Typography>
              <Typography>{formatAmount(caseData.TRANSACTION_AMOUNT)}</Typography>
            </Grid>
          </Grid>

          {(showLetterSection || isEditing) && (
            <>
          <Divider sx={{ my: 3 }} />

          {isEditing ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Edit the dispute letter content below. You can modify the letter or write your own.
              </Typography>
              <Box sx={{ 
                '& .ql-container': {
                  minHeight: '400px',
                  fontSize: '0.875rem',
                  overflow: 'visible',
                },
                '& .ql-editor': {
                  minHeight: '400px',
                  overflow: 'visible',
                }
              }}>
                <ReactQuill
                  theme="snow"
                  value={letterContent}
                  onChange={handleContentChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter your dispute letter content here..."
                />
              </Box>
            </Box>
          ) : (
            <Box>
              {hasChanges && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This letter has been customized. Click "Edit Letter" to make further changes.
                </Alert>
              )}
              <Box
                sx={{
                  '& .dispute-letter-formal': {
                    fontSize: '0.95rem',
                    lineHeight: 1.95,
                    color: '#1f2937',
                  },
                  '& .dispute-letter-formal h2': {
                    color: '#111827',
                  },
                  '& .dispute-letter-formal p': {
                    marginBottom: '0.65rem',
                    textAlign: 'justify',
                  },
                  '& .dispute-letter-body p': {
                    marginBottom: '1rem',
                    textAlign: 'justify',
                  },
                  '& .dispute-letter-body ul, & .dispute-letter-body ol': {
                    marginBottom: '1rem',
                    paddingLeft: '1.5rem',
                  },
                  '& .dispute-letter-body li': {
                    marginBottom: '0.5rem',
                  },
                  '& .ql-editor': {
                    padding: 0,
                    fontSize: '0.95rem',
                    lineHeight: 1.95,
                    color: '#1f2937',
                  },
                  '& .ql-editor p': {
                    marginBottom: '1rem',
                    textAlign: 'justify',
                  },
                  '& .ql-editor ul, & .ql-editor ol': {
                    marginBottom: '1rem',
                    paddingLeft: '1.5rem',
                  },
                  '& .ql-editor li': {
                    marginBottom: '0.5rem',
                  },
                }}
                dangerouslySetInnerHTML={{ __html: letterContent }}
              />
              {!hasChanges && !latestAiLetterContent && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This is the default template. Click &quot;Generate AI Letter&quot; to replace the body with an AI draft from case evidence.
                </Alert>
              )}
            </Box>
          )}
            </>
          )}

          {!showLetterSection && !isEditing && !isDraftLoading && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Use &quot;Edit Letter&quot; for the starter template, or &quot;Generate AI Letter&quot; for a draft from evidence. PDF actions are available once there is letter content.
            </Alert>
          )}
        </Box>
        )}
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.duration || 6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}


